import { STATS_TUNING } from './tuning.js';
import { STATS, type Action, type Stat, type StatArray, type StatVector } from './types.js';

// tuning.STATS_TUNING の別名 re-export (後方互換)
export const DECAY_HALF_LIFE_DAYS = STATS_TUNING.decayHalfLifeDays;
export const MIN_STAT_VALUE = STATS_TUNING.minStatValue;
export const DAILY_CAP_PER_ACTION_TYPE = STATS_TUNING.dailyCapPerActionType;

/** 空のステータスベクトル */
export function zeroStats(): StatVector {
  return { atk: 0, def: 0, agi: 0, int: 0, luk: 0 };
}

/** 加算 */
export function addStats(a: StatVector, b: StatVector): StatVector {
  return {
    atk: a.atk + b.atk,
    def: a.def + b.def,
    agi: a.agi + b.agi,
    int: a.int + b.int,
    luk: a.luk + b.luk,
  };
}

/** スカラー倍 */
export function scaleStats(v: StatVector, k: number): StatVector {
  return { atk: v.atk * k, def: v.def * k, agi: v.agi * k, int: v.int * k, luk: v.luk * k };
}

/**
 * アクションを時間減衰込みで集計し、生のステータスを計算する。
 * 半減期 DECAY_HALF_LIFE_DAYS。
 */
export function currentStatsRaw(actions: readonly Action[], now: number = Date.now()): StatVector {
  let acc = zeroStats();
  for (const a of actions) {
    const ageDays = (now - a.timestamp) / 86400000;
    const decay = Math.exp((-ageDays * Math.LN2) / DECAY_HALF_LIFE_DAYS);
    acc = addStats(acc, scaleStats(a.weights, decay));
  }
  return acc;
}

/** 床値 (各軸で最低 MIN_STAT_VALUE) を適用 */
export function applyFloor(v: StatVector): StatVector {
  const out: Partial<StatVector> = {};
  for (const s of STATS) out[s] = Math.max(MIN_STAT_VALUE, v[s]);
  return out as StatVector;
}

/**
 * 正規化 (合計 100)。
 * 入力が全 0 の場合は均等配分。
 */
export function normalizeStats(v: StatVector): StatVector {
  const total = STATS.reduce((s, k) => s + v[k], 0);
  if (total === 0) return { atk: 20, def: 20, agi: 20, int: 20, luk: 20 };
  const ratio = 100 / total;
  return {
    atk: Math.round(v.atk * ratio),
    def: Math.round(v.def * ratio),
    agi: Math.round(v.agi * ratio),
    int: Math.round(v.int * ratio),
    luk: Math.round(v.luk * ratio),
  };
}

/**
 * 日次上限適用: 同日・同アクション種別の 6 回目以降は weights=0 にする。
 */
export function capDailyActions(actions: readonly Action[]): Action[] {
  const byDay = new Map<string, Map<string, number>>();
  const result: Action[] = [];
  for (const a of actions) {
    const day = new Date(a.timestamp).toISOString().slice(0, 10);
    if (!byDay.has(day)) byDay.set(day, new Map());
    const counters = byDay.get(day)!;
    const count = counters.get(a.type) ?? 0;
    counters.set(a.type, count + 1);
    if (count >= DAILY_CAP_PER_ACTION_TYPE) {
      result.push({ ...a, weights: { atk: 0, def: 0, agi: 0, int: 0, luk: 0 } });
    } else {
      result.push(a);
    }
  }
  return result;
}

/** StatVector → 5 要素タプル (atk, def, agi, int, luk) */
export function toStatArray(v: StatVector): StatArray {
  return [v.atk, v.def, v.agi, v.int, v.luk] as const;
}

export function statAsArray(v: StatVector): [number, number, number, number, number] {
  return [v.atk, v.def, v.agi, v.int, v.luk];
}

/** 全体パイプライン: actions → 生データ → 床 → 正規化 */
export function computeStats(actions: readonly Action[], now: number = Date.now()): StatVector {
  const capped = capDailyActions(actions);
  const raw = currentStatsRaw(capped, now);
  const floored = applyFloor(raw);
  return normalizeStats(floored);
}

/** ステータスの差分ギャップ (目標 - 現在)、軸ごと */
export function statGap(current: StatVector, target: StatVector): StatVector {
  return {
    atk: target.atk - current.atk,
    def: target.def - current.def,
    agi: target.agi - current.agi,
    int: target.int - current.int,
    luk: target.luk - current.luk,
  };
}

/**
 * 「target に対する相対ギャップ」が大きい順に Stat キーを返す。
 *
 * 各 stat の target 値域は職業ごとに 7〜44 と 6 倍の幅があるため、絶対点数で
 * 比較するとレンジが小さい stat (例: 遊び人 int=10) のオーバー/アンダーが
 * 過小評価される。`|gap| / target` で正規化して相対的な歪みの大きさで並べる。
 *
 * target は常に正 (最小 7) なので 0 除算は発生しないが、防御として
 * `Math.max(target[s], 1)` を入れている。
 */
export function sortStatsByRelativeGap(gap: StatVector, target: StatVector): readonly Stat[] {
  return [...STATS].sort((a, b) =>
    Math.abs(gap[b]) / Math.max(target[b], 1) - Math.abs(gap[a]) / Math.max(target[a], 1),
  );
}
